import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContextType';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState<string | null>(null);
  const hasAttemptedVerification = useRef(false);

  const verifyToken = useCallback(async (token: string) => {
    if (hasAttemptedVerification.current) return;
    hasAttemptedVerification.current = true;

    try {
      await verifyEmail(token);
      setStatus('success');
    } catch (error) {
      setStatus('error');
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to verify email. Please try again.');
      }
    }
  }, [verifyEmail]);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setError('No verification token found.');
      return;
    }

    verifyToken(token);
  }, [searchParams, verifyToken]);

  return (
    <Container>
      <Card>
        <Logo>PEI App</Logo>
        {status === 'verifying' && (
          <>
            <Title>Verifying Email</Title>
            <Message>Please wait while we verify your email address...</Message>
          </>
        )}
        
        {status === 'success' && (
          <>
            <Title>Email Verified!</Title>
            <Message>Your email has been successfully verified.</Message>
            <Button onClick={() => navigate('/login')}>
              Proceed to Login
            </Button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <Title>Verification Failed</Title>
            <ErrorMessage>{error}</ErrorMessage>
            <Button onClick={() => navigate('/login')}>
              Back to Login
            </Button>
          </>
        )}
      </Card>
    </Container>
  );
};

export default EmailVerification;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const Card = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Logo = styled.h1`
  color: #333;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  margin-bottom: 1.5rem;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }
`; 